import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API from '../../api/axiosConfig';
import QuestionDraftForm from './QuestionDraftForm';
import MathText from '../../components/MathText';
import TopicSelect from '../../components/TopicSelect';

const autoFormatMath = (text) => {
  if (!text) return '';
  if (text.includes('$')) return text; 
  return text
    .replace(/\b(\d+)\/(\d+)\b/g, '$\\frac{$1}{$2}$') 
    .replace(/([a-zA-Zа-яА-Я])\^(\d+)/g, '$$$1^{$2}$$') 
    .replace(/>=/g, '$\\ge$')                        
    .replace(/<=/g, '$\\le$')                        
    .replace(/!=/g, '$\\neq$');                      
};

function QuestionsManager() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const isAddMode = searchParams.get('action') === 'add';
  const addModeType = searchParams.get('mode') || 'manual'; 
  const isSummaryMode = searchParams.get('action') === 'summary';
  
  const { userData } = useAuth(); 
  const isAdmin = userData?.role === 'ADMIN' || userData?.is_staff;
  const currentUserId = userData?.id;

  const [questions, setQuestions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]); 
  const [loading, setLoading] = useState(true);
  
  const [activeTab, setActiveTab] = useState('private'); 
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterCreator, setFilterCreator] = useState('');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, private: 0, public: 0 });
  const [creatorsList, setCreatorsList] = useState([]); 
  
  const [summaryStats, setSummaryStats] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);

  const answerLabels = ['А', 'Б', 'В', 'Г', 'Д'];
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const [scanFile, setScanFile] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [draftQuestions, setDraftQuestions] = useState([]);
  const [scanConfig, setScanConfig] = useState({ subject: '', class_level: 5, difficulty: 'Базовый', topic: '' });
  
  const [formErrors, setFormErrors] = useState({});
  const [editingManualAnsIdx, setEditingManualAnsIdx] = useState(-1);
  const [isEditingManualText, setIsEditingManualText] = useState(true);

  // Стейт для всплывающего предупреждения "Выберите предмет"
  const [subjectWarning, setSubjectWarning] = useState(false);

  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const [newQuestion, setNewQuestion] = useState({
    id: null, text: '', subject: '', class_level: 5, difficulty: 'Базовый', topic: '', image: null,
    answers: [
      { text: '', is_correct: true }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }
    ]
  });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => { setToast(null); }, 3000);
  }, []);

  // ФУНКЦИЯ ВЫЗОВА УВЕДОМЛЕНИЯ ПРЕДМЕТА
  const handleTopicDisabledClick = () => {
    setSubjectWarning(true);
    setTimeout(() => setSubjectWarning(false), 3000);
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 600);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => { setCurrentPage(1); }, [activeTab, debouncedSearch, filterSubject, filterCreator]);

  const fetchData = useCallback(async () => {
    if (!userData) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = { page: currentPage, tab: activeTab, search: debouncedSearch, subject: filterSubject, creator: filterCreator };
      const [questionsRes, subjectsRes, topicsRes] = await Promise.all([
        API.get('/api/questions/', { params }), API.get('/api/subjects/'), API.get('/api/topics/')
      ]);
      
      if (questionsRes.data && questionsRes.data.results) {
          setQuestions(questionsRes.data.results);
          setTotalPages(questionsRes.data.total_pages || 1);
          setStats({ total: questionsRes.data.count || 0, private: questionsRes.data.my_private || 0, public: questionsRes.data.my_public || 0 });
      } else {
          const allData = questionsRes.data || [];
          setQuestions(allData);
          setStats({ total: allData.length, private: allData.filter(q => q.creator === currentUserId && !q.is_public).length, public: allData.filter(q => q.creator === currentUserId && q.is_public).length });
      }
      setSubjects(subjectsRes.data); setTopics(topicsRes.data);
      if (isAdmin && creatorsList.length === 0) {
          API.get('/api/questions-stats/').then(res => setCreatorsList(res.data.users || []));
      }
    } catch (error) { console.error(error); }
    setLoading(false);
  }, [userData, currentPage, activeTab, debouncedSearch, filterSubject, filterCreator, isAdmin, creatorsList.length, currentUserId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (isSummaryMode) {
        const fetchSum = async () => {
            setIsSummaryLoading(true);
            try {
                const [qRes, sRes] = await Promise.all([
                    API.get('/api/questions/'), 
                    API.get('/api/subjects/')
                ]);
                
                const total = qRes.data.count || 0;
                const myPrivate = qRes.data.my_private || 0;
                const myPublic = qRes.data.my_public || 0;
                const subs = sRes.data || [];

                const subjectPromises = subs.map(sub => API.get(`/api/questions/?subject=${sub.id}`));
                const subjectResponses = await Promise.all(subjectPromises);

                let bySubject = subs.map((sub, index) => {
                    const rData = subjectResponses[index].data;
                    const subTotal = rData.count || 0;
                    const subMyPrivate = rData.my_private || 0;
                    const subMyPublic = rData.my_public || 0;

                    return { 
                        name: sub.name, 
                        total: subTotal, 
                        public: subTotal - subMyPrivate,
                        mine: subMyPrivate + subMyPublic
                    };
                });

                bySubject.sort((a, b) => b.mine - a.mine || b.total - a.total || a.name.localeCompare(b.name));
                setSummaryStats({ total, myPrivate, myPublic, bySubject, subjectsCount: subs.length });
            } catch (e) { console.error(e); }
            setIsSummaryLoading(false);
        };
        fetchSum();
    }
  }, [isSummaryMode]);

  let displayQuestions = questions;
  let displayTotalPages = totalPages;

  if (questions.length > 50) {
      const clientFiltered = questions.filter(q => {
          if (debouncedSearch && !q.text.toLowerCase().includes(debouncedSearch.toLowerCase())) return false;
          if (filterSubject && q.subject !== parseInt(filterSubject)) return false;
          if (isAdmin && filterCreator && q.creator !== parseInt(filterCreator)) return false;
          if (isAdmin) {
            if (activeTab === 'private') return q.creator === currentUserId;
            if (activeTab === 'public') return q.is_public;
            if (activeTab === 'blocked') return q.is_blocked;
          } else {
            if (activeTab === 'private') return q.creator === currentUserId;
            if (activeTab === 'public') return q.is_public && !q.is_blocked;
            if (activeTab === 'all') return (q.creator === currentUserId) || (q.is_public && !q.is_blocked);
          }
          return true;
      });
      displayTotalPages = Math.ceil(clientFiltered.length / 20) || 1;
      const idxLast = currentPage * 20;
      const idxFirst = idxLast - 20;
      displayQuestions = clientFiltered.slice(idxFirst, idxLast);
  }

  // --- ACTIONS ---
  const handleFileChange = (e) => { if (e.target.files && e.target.files[0]) setSelectedFile(e.target.files[0]); };
  
  const handleBulkUpload = async () => {
    if (!selectedFile) return;
    setIsUploading(true);
    const formData = new FormData(); formData.append('file', selectedFile);
    try {
      const res = await API.post('/api/import-excel/', formData, { headers: { 'Content-Type': 'multipart/form-data' }});
      showToast(res.data.message || "Успешно импортировано!");
      setSelectedFile(null); 
      setTimeout(() => navigate('/questions'), 1500);
    } catch (error) { showToast("Ошибка при загрузке Excel файла.", "error"); } 
    finally { setIsUploading(false); }
  };

  const handleScanFileChange = (e) => { if (e.target.files && e.target.files[0]) setScanFile(e.target.files[0]); };
  
  const handleStartAILocalScan = async () => {
    if (!scanFile) return;
    setIsScanning(true);
    const formData = new FormData(); formData.append('file', scanFile);
    const token = localStorage.getItem('access_token'); 

    try {
      const response = await API.post('/api/ai-scan-image/', formData, { headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }, timeout: 90000 });
      if (response.data && response.data.questions && response.data.questions.length > 0) {
        const enrichedQuestions = response.data.questions.map(q => {
            let aiAnswers = q.answers;
            if (!aiAnswers || aiAnswers.length === 0) {
                aiAnswers = [ { text: '', is_correct: true }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false } ];
            }
            return { ...q, default_subject: scanConfig.subject, default_class_level: scanConfig.class_level, default_difficulty: scanConfig.difficulty, default_topic: scanConfig.topic, answers: aiAnswers };
        });
        setDraftQuestions(enrichedQuestions);
      } else { showToast("ИИ не нашел вопросов на изображении.", "error"); }
      setScanFile(null);
    } catch (error) { 
        if (error.code === 'ECONNABORTED') showToast("Превышено время ожидания.", "error");
        else showToast(`Ошибка: ${error.response?.data?.error || "Сервер не смог обработать изображение."}`, "error"); 
    } finally { setIsScanning(false); }
  };

  const saveQuestionToBackend = async (questionData, isEdit = false, questionId = null) => {
    const cleanedAnswers = questionData.answers.map(a => {
      const ans = { text: a.text, is_correct: a.is_correct };
      if (a.id) ans.id = a.id;
      return ans;
    });
    const payload = { text: questionData.text, subject: questionData.subject, class_level: questionData.class_level, difficulty: questionData.difficulty, answers: cleanedAnswers, topic: questionData.topic || null };
    if (questionData.image) {
      const formData = new FormData();
      formData.append('text', payload.text); formData.append('subject', payload.subject); formData.append('class_level', payload.class_level); formData.append('difficulty', payload.difficulty);
      if (payload.topic) formData.append('topic', payload.topic);
      formData.append('answers', JSON.stringify(payload.answers));
      if (typeof questionData.image !== 'string') formData.append('image', questionData.image);
      if (isEdit) return await API.patch(`/api/questions/${questionId}/`, formData, { headers: { 'Content-Type': 'multipart/form-data' }});
      return await API.post('/api/questions/', formData, { headers: { 'Content-Type': 'multipart/form-data' }});
    } else {
      if (isEdit) return await API.patch(`/api/questions/${questionId}/`, payload);
      return await API.post('/api/questions/', payload);
    }
  };

  const handleSaveDraft = async (draftData, indexToDrop) => {
    let errorsList = [];
    if (!draftData.subject) errorsList.push("Выберите предмет");
    if (!draftData.text?.trim()) errorsList.push("Текст вопроса не может быть пустым");
    const emptyAnswers = draftData.answers?.filter(a => !a.text?.trim()) || [];
    if (!draftData.answers || draftData.answers.length === 0) errorsList.push("Добавьте варианты ответов");
    else if (emptyAnswers.length > 0) errorsList.push("Заполните пустые ячейки ответов");

    if (errorsList.length > 0) { showToast("⚠️ Ошибка:\n- " + errorsList.join('\n- '), "error"); return; }
    try {
      await saveQuestionToBackend(draftData);
      setDraftQuestions(prev => prev.filter((_, i) => i !== indexToDrop));
      showToast("✅ Вопрос успешно сохранен!");
    } catch (error) { showToast(`❌ Ошибка сохранения`, "error"); }
  };

  const handleDiscardDraft = (indexToDrop) => { setDraftQuestions(prev => prev.filter((_, i) => i !== indexToDrop)); };

  const handleAnswerTextChange = (index, text) => {
    const updatedAnswers = [...newQuestion.answers];
    updatedAnswers[index].text = text; setNewQuestion({ ...newQuestion, answers: updatedAnswers });
    if (formErrors[`answer_${index}`]) setFormErrors({...formErrors, [`answer_${index}`]: false});
  };
  
  const handleCorrectAnswerChange = (selectedIndex) => {
    const updatedAnswers = newQuestion.answers.map((ans, idx) => ({ ...ans, is_correct: idx === selectedIndex }));
    setNewQuestion({ ...newQuestion, answers: updatedAnswers });
  };
  
  const handleCreateQuestion = async (e) => {
    e.preventDefault(); 
    const errors = {};
    if (!newQuestion.subject) errors.subject = true;
    if (!newQuestion.text.trim()) errors.text = true;
    let hasEmptyAnswers = false;
    newQuestion.answers.forEach((ans, idx) => { if (!ans.text.trim()) { errors[`answer_${idx}`] = true; hasEmptyAnswers = true; } });

    if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        let msg = "Исправьте ошибки:\n" + (errors.subject ? "- Выберите предмет\n" : "") + (errors.text ? "- Введите текст\n" : "") + (hasEmptyAnswers ? "- Заполните ячейки" : "");
        showToast("⚠️ " + msg, "error"); return;
    }

    try {
      if (newQuestion.id) {
        await saveQuestionToBackend(newQuestion, true, newQuestion.id);
        showToast("Вопрос успешно обновлен!");
      } else {
        await saveQuestionToBackend(newQuestion);
        showToast("Вопрос успешно добавлен в ваш личный банк!");
      }

      setNewQuestion({ id: null, text: '', subject: '', class_level: 5, difficulty: 'Базовый', topic: '', image: null, answers: [{ text: '', is_correct: true }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }] });
      setFormErrors({});
      setIsEditingManualText(true);
      setTimeout(() => navigate('/questions'), 1500);
    } catch (error) { 
        showToast(`❌ Ошибка сервера`, "error"); 
    }
  };

  const handleCancelEdit = () => {
    setNewQuestion({ id: null, text: '', subject: '', class_level: 5, difficulty: 'Базовый', topic: '', image: null, answers: [{ text: '', is_correct: true }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }] });
    setFormErrors({});
    setIsEditingManualText(true);
    navigate('/questions');
  };

  const handleEditQuestion = (question) => {
    navigate('/questions?action=add&mode=manual');
    setNewQuestion({
        id: question.id,
        text: question.text,
        subject: question.subject,
        class_level: question.class_level,
        difficulty: question.difficulty || 'Базовый',
        topic: question.topic || '',
        image: question.image || null, 
        answers: question.answers && question.answers.length >= 5 ? question.answers.slice(0, 5) : [{ text: '', is_correct: true }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }]
    });
    setIsEditingManualText(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm("Удалить этот вопрос?")) return;
    try { await API.delete(`/api/questions/${id}/`); fetchData(); showToast("Вопрос удален!"); } catch (error) { showToast("Ошибка удаления", "error"); }
  };
  const handleSendToModeration = async (id) => { try { await API.post(`/api/questions/${id}/send_to_moderation/`); fetchData(); showToast("Отправлено на модерацию!"); } catch (error) {} };
  const handleCloneQuestion = async (id) => { try { await API.post(`/api/questions/${id}/clone/`); setActiveTab('private'); fetchData(); showToast("Вопрос скопирован!");} catch (error) {} };
  const handleToggleBlock = async (id) => { try { await API.post(`/api/questions/${id}/toggle_block/`); fetchData(); } catch (error) {} };
  const handleApproveQuestion = async (id) => { try { await API.post(`/api/questions/${id}/approve/`); fetchData(); showToast("Одобрено!"); } catch (error) {} };

  const handleManualPasteImage = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
            const blob = items[i].getAsFile();
            setNewQuestion({ ...newQuestion, image: blob });
            showToast("📸 Скриншот успешно вставлен!");
            e.preventDefault();
            break;
        }
    }
  };

  // =========================================================================
  // РЕНДЕР: ДЕТАЛЬНАЯ СВОДКА ПЛАТФОРМЫ
  // =========================================================================
  const renderSummaryMode = () => {
    if (isSummaryLoading || !summaryStats) {
        return <div className="flex justify-center items-center min-h-[60vh]"><div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
    }

    return (
        <div className="max-w-6xl mx-auto mt-6 space-y-6">
            <div className="flex justify-between items-center bg-white p-5 rounded-3xl border border-gray-200 shadow-sm">
                <h1 className="text-2xl font-black text-gray-800 flex items-center gap-3"><span>📊</span> Сводка платформы</h1>
                <button onClick={() => navigate('/questions')} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold px-5 py-2.5 rounded-xl shadow-sm transition-colors">← К списку вопросов</button>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm flex flex-col items-center justify-center">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Всего в базе</p>
                    <p className="text-4xl font-black text-blue-600">{summaryStats.total}</p>
                </div>
                <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm flex flex-col items-center justify-center">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Предметов</p>
                    <p className="text-4xl font-black text-purple-600">{summaryStats.subjectsCount}</p>
                </div>
                <div className="bg-indigo-50/50 p-6 rounded-3xl border border-indigo-100 shadow-sm flex flex-col items-center justify-center">
                    <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Личный банк</p>
                    <p className="text-4xl font-black text-indigo-600">{summaryStats.myPrivate}</p>
                </div>
                <div className="bg-green-50/50 p-6 rounded-3xl border border-green-100 shadow-sm flex flex-col items-center justify-center">
                    <p className="text-[10px] font-black text-green-500 uppercase tracking-widest mb-1">Опубликовано мной</p>
                    <p className="text-4xl font-black text-green-600">{summaryStats.myPublic}</p>
                </div>
            </div>

            <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <h2 className="text-lg font-black text-gray-800">Детализация по предметам</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 p-6 bg-gray-50/50">
                    {summaryStats.bySubject.length === 0 ? (
                        <div className="col-span-full text-center text-gray-400 py-10 font-bold">База вопросов пуста</div>
                    ) : (
                        summaryStats.bySubject.map((sub, idx) => (
                            <div key={idx} className={`bg-white p-5 rounded-2xl transition-all ${sub.mine > 0 ? 'border-2 border-blue-200 shadow-md ring-2 ring-blue-50' : 'border border-gray-100 shadow-sm hover:shadow-md'}`}>
                                <h3 className="text-base font-black text-gray-800 mb-4 flex justify-between items-center">
                                    {sub.name}
                                    {sub.mine > 0 && <span className="text-[9px] bg-blue-100 text-blue-700 px-2.5 py-1 rounded-md uppercase tracking-widest">Мой фокус</span>}
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-gray-500 font-bold">Всего вопросов</span>
                                        <span className="font-black text-blue-600 bg-blue-50 px-2.5 py-1 rounded-lg">{sub.total}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-gray-500 font-bold">В общем доступе</span>
                                        <span className="font-black text-green-600 bg-green-50 px-2.5 py-1 rounded-lg">{sub.public}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs border-t border-gray-50 pt-3">
                                        <span className="text-gray-500 font-bold">Мои вопросы</span>
                                        <span className="font-black text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg">{sub.mine}</span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
  };

  // =========================================================================
  // РЕНДЕР: ЧЕРНОВИКИ AI
  // =========================================================================
  const renderDrafts = () => (
    <div className="max-w-[1600px] mx-auto space-y-6 bg-gray-50 p-4 md:p-8 rounded-3xl">
        <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-5 md:p-6 rounded-[2rem] shadow-sm border border-gray-100 gap-4">
            <div>
                <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2">🤖 AI-Ассистент</h1>
                <p className="text-sm text-gray-500 font-medium mt-1">Распознано: <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-bold">{draftQuestions.length}</span>.</p>
            </div>
            <button onClick={() => setDraftQuestions([])} className="bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-red-600 px-6 py-3 rounded-xl font-bold text-sm transition-all">Отменить всё</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-stretch">
            {draftQuestions.map((draft, idx) => (
                <QuestionDraftForm key={idx} index={idx} initialData={draft} subjects={subjects} topics={topics} onSave={handleSaveDraft} onDiscard={handleDiscardDraft} />
            ))}
        </div>
    </div>
  );

  // =========================================================================
  // РЕНДЕР: ДОБАВЛЕНИЕ ВОПРОСОВ 
  // =========================================================================
  const renderAddMode = () => {
    return (
      <div className="max-w-4xl mx-auto mt-6">
        {addModeType === 'excel' && (
          <div>
              <div className="flex justify-between items-center mb-6">
                  <h1 className="text-2xl font-black text-gray-800">Оптовая загрузка Excel</h1>
                  <button onClick={() => navigate('/questions')} className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-bold px-5 py-2.5 rounded-xl shadow-sm transition-colors">← К списку</button>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-[2rem] p-12 shadow-xl text-center text-white relative overflow-hidden">
                  <h2 className="text-3xl font-black mb-4">Массовый импорт</h2>
                  <p className="text-blue-100 mb-8 font-medium">Загрузите готовый файл формата .xlsx или .csv для автоматического переноса вопросов в базу.</p>
                  
                  <div className="relative max-w-sm mx-auto">
                      <input type="file" id="bulk-file" accept=".xlsx, .xls, .csv" onChange={handleFileChange} className="hidden" />
                      {!selectedFile ? (
                          <label htmlFor="bulk-file" className="block w-full bg-white/20 hover:bg-white/30 text-white font-black text-sm px-6 py-5 rounded-xl cursor-pointer transition-all border-2 border-dashed border-white/40 hover:border-white shadow-sm">
                              Выбрать файл на компьютере
                          </label>
                      ) : (
                          <div className="bg-white/20 p-6 rounded-2xl border border-white/30 backdrop-blur-md shadow-lg">
                              <p className="text-sm font-bold truncate mb-6">{selectedFile.name}</p>
                              <div className="flex gap-3">
                                  <button onClick={() => setSelectedFile(null)} disabled={isUploading} className="flex-1 py-3 bg-gray-900/40 hover:bg-gray-900/60 text-white text-xs font-bold rounded-xl transition-colors">Отмена</button>
                                  <button onClick={handleBulkUpload} disabled={isUploading} className="flex-1 py-3 bg-white hover:bg-gray-100 text-blue-600 text-xs font-black rounded-xl shadow-md transition-colors">
                                      {isUploading ? 'Загрузка...' : 'Загрузить в базу'}
                                  </button>
                              </div>
                          </div>
                      )}
                  </div>
              </div>
          </div>
        )}

        {addModeType === 'ai' && (
          <div>
              <div className="flex justify-between items-center mb-6">
                  <h1 className="text-2xl font-black text-gray-800">Распознавание по фото</h1>
                  <button onClick={() => navigate('/questions')} className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-bold px-5 py-2.5 rounded-xl shadow-sm transition-colors">← К списку</button>
              </div>
              
              <div className="bg-gradient-to-r from-purple-800 to-indigo-900 p-8 rounded-[2rem] shadow-xl text-white">
                  <div className="flex flex-col md:flex-row items-center gap-6">
                      <div className="flex items-center gap-4 md:w-1/3 shrink-0">
                          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-3xl shadow-inner border border-white/10">🤖</div>
                          <div>
                              <h2 className="text-lg font-black leading-tight">AI-Ассистент</h2>
                              <p className="text-indigo-200 text-xs font-medium mt-1">умная оцифровка</p>
                          </div>
                      </div>
                      
                      <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="relative w-full">
                              {subjectWarning && (
                                  <div className="absolute -top-10 left-0 bg-red-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap animate-bounce z-10">
                                      Сначала выберите предмет!
                                      <div className="absolute -bottom-1 left-4 w-2 h-2 bg-red-500 rotate-45"></div>
                                  </div>
                              )}
                              <select 
                                className={`w-full px-4 py-3 rounded-xl ${subjectWarning ? 'bg-red-50 border-red-500 text-red-700 ring-2 ring-red-200' : 'bg-white/10 border-indigo-500/50 text-white'} border text-sm font-bold outline-none transition-all`} 
                                value={scanConfig.subject} 
                                onChange={(e) => { 
                                    setScanConfig({ ...scanConfig, subject: e.target.value, topic: '' }); 
                                    setSubjectWarning(false); 
                                }}
                              >
                                <option value="" className="text-gray-900">Выбрать предмет...</option>
                                {subjects.map(sub => <option key={sub.id} value={sub.id} className="text-gray-900">{sub.name}</option>)}
                              </select>
                          </div>
                          
                          <select className="w-full px-4 py-3 rounded-xl bg-white/10 border border-indigo-500/50 text-sm font-bold outline-none" value={scanConfig.class_level} onChange={(e) => setScanConfig({ ...scanConfig, class_level: parseInt(e.target.value) })}>
                              {[5, 6, 7, 8, 9, 10, 11].map(lvl => <option key={lvl} value={lvl} className="text-gray-900">{lvl} класс</option>)}
                          </select>
                          <select className="w-full px-4 py-3 rounded-xl bg-white/10 border border-indigo-500/50 text-sm font-bold outline-none" value={scanConfig.difficulty} onChange={(e) => setScanConfig({ ...scanConfig, difficulty: e.target.value })}>
                              {['Базовый', 'Повышенный', 'Высокий'].map(diff => <option key={diff} value={diff} className="text-gray-900">{diff}</option>)}
                          </select>
                          <div className="w-full text-gray-900 text-sm font-bold">
                              <TopicSelect 
                                selectedSubjectId={scanConfig.subject} 
                                value={scanConfig.topic} 
                                onChange={(val) => setScanConfig({ ...scanConfig, topic: val })} 
                                currentUser={userData} 
                                className="w-full h-full min-h-[46px] px-4 py-3 rounded-xl bg-white/10 border border-indigo-500/50 outline-none text-white hover:bg-white/20 transition-colors" 
                                onDisabledClick={handleTopicDisabledClick}
                              />
                          </div>
                      </div>
                      
                      <div className="flex flex-col gap-3 w-full md:w-auto shrink-0 md:pl-6 md:border-l border-white/20">
                          <input type="file" id="ai-scan-file" accept="image/*" onChange={handleScanFileChange} className="hidden" />
                          <label htmlFor="ai-scan-file" className={`text-center bg-white/10 hover:bg-white/20 text-sm font-bold px-6 py-4 rounded-xl cursor-pointer transition-all border border-white/20 shadow-sm`}>
                              {scanFile ? `✅ Фото загружено` : '📷 Выбрать фото'}
                          </label>
                          <button onClick={handleStartAILocalScan} disabled={!scanFile || isScanning} className={`text-sm font-black px-6 py-4 rounded-xl shadow-lg transition-colors ${!scanFile ? 'bg-gray-500/50 text-gray-300 cursor-not-allowed' : 'bg-purple-500 hover:bg-purple-400 text-white'}`}>
                              {isScanning ? '⏳ Обработка...' : '⚡ Распознать текст'}
                          </button>
                      </div>
                  </div>
              </div>
          </div>
        )}

        {addModeType === 'manual' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-black text-gray-800">✍️ {newQuestion.id ? 'Редактирование вопроса' : 'Ручное создание вопроса'}</h1>
              <button onClick={() => navigate('/questions')} className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-bold px-4 py-2.5 rounded-xl shadow-sm transition-colors">← К списку</button>
            </div>
            
            <div className="p-8 rounded-[2rem] border border-gray-200 shadow-sm bg-white">
              <form onSubmit={handleCreateQuestion} className="space-y-6">
                <div className={`min-h-[80px] rounded-2xl p-4 border transition-colors ${formErrors.text ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-gray-50/50'}`}>
                  <div className="flex justify-between items-center mb-2">
                      <span className={`text-[10px] font-black uppercase tracking-widest ${formErrors.text ? 'text-red-500' : 'text-gray-400'}`}>Текст вопроса</span>
                      {!isEditingManualText && (
                          <button type="button" onClick={() => setIsEditingManualText(true)} className="text-xs text-blue-500 font-bold hover:underline">✎ Изменить текст</button>
                      )}
                  </div>
                  {isEditingManualText ? (
                      <div className="space-y-3">
                          <button type="button" onClick={() => { const tableTemplate = "\n$$ \\begin{array}{|c|c|c|} \\hline x & y & z \\\\ \\hline 1 & 2 & 3 \\\\ \\hline \\end{array} $$\n"; setNewQuestion({ ...newQuestion, text: newQuestion.text + tableTemplate }); }} className="bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm flex items-center gap-2 transition-colors">📊 Вставить таблицу</button>
                          <div className="w-full rounded-xl border border-blue-300 focus-within:ring-2 focus-within:ring-blue-500/20 bg-white p-3 flex flex-col gap-3">
                              <textarea rows="4" className="w-full outline-none text-sm text-gray-800 bg-transparent resize-y" placeholder="Текст вопроса... (вставьте скриншот графика через Ctrl+V)" value={newQuestion.text} onChange={(e) => { setNewQuestion({ ...newQuestion, text: e.target.value }); if(formErrors.text) setFormErrors({...formErrors, text: false}); }} onPaste={handleManualPasteImage} autoFocus />
                              {newQuestion.image && (
                                  <div className="relative self-start inline-block border border-gray-200 rounded-lg p-2 bg-gray-50">
                                      {typeof newQuestion.image === 'string' ? ( <img src={newQuestion.image} alt="Скриншот" className="max-h-40 object-contain rounded" /> ) : ( <img src={URL.createObjectURL(newQuestion.image)} alt="Скриншот" className="max-h-40 object-contain rounded" /> )}
                                      <button type="button" onClick={() => setNewQuestion({...newQuestion, image: null})} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] font-bold shadow-md hover:bg-red-600">✕</button>
                                  </div>
                              )}
                          </div>
                          {newQuestion.text && (
                            <div className="px-4 py-3 bg-indigo-50/50 border border-indigo-100 rounded-xl text-sm text-gray-800">
                              <span className="text-[10px] text-indigo-400 font-bold uppercase tracking-widest block mb-2">Предпросмотр:</span>
                              <MathText text={autoFormatMath(newQuestion.text)} />
                            </div>
                          )}
                          <button type="button" onClick={() => setIsEditingManualText(false)} className="w-full py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-bold rounded-lg uppercase tracking-widest transition-colors">Сохранить текст</button>
                      </div>
                  ) : (
                      <div onClick={() => setIsEditingManualText(true)} className="cursor-pointer min-h-[60px] p-4 bg-white rounded-xl border border-transparent hover:border-gray-200 transition-colors" title="Нажмите для редактирования">
                          <MathText text={autoFormatMath(newQuestion.text) || "Пустой вопрос"} />
                          {newQuestion.image && (
                              <div className="mt-4">
                                  {typeof newQuestion.image === 'string' ? ( <img src={newQuestion.image} alt="Скриншот" className="max-h-40 object-contain rounded-xl border border-gray-200 shadow-sm" /> ) : ( <img src={URL.createObjectURL(newQuestion.image)} alt="Скриншот" className="max-h-40 object-contain rounded-xl border border-gray-200 shadow-sm" /> )}
                              </div>
                          )}
                      </div>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="relative w-full">
                    {subjectWarning && (
                        <div className="absolute -top-10 left-0 bg-red-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg shadow-lg whitespace-nowrap animate-bounce z-10">
                            Сначала выберите предмет!
                            <div className="absolute -bottom-1 left-4 w-2 h-2 bg-red-500 rotate-45"></div>
                        </div>
                    )}
                    <select 
                        className={`w-full px-4 py-3 rounded-xl border ${formErrors.subject || subjectWarning ? 'border-red-500 bg-red-50 ring-2 ring-red-200' : 'border-gray-200 bg-white'} text-sm font-bold outline-none transition-all`} 
                        value={newQuestion.subject} 
                        onChange={(e) => { 
                            setNewQuestion({ ...newQuestion, subject: e.target.value, topic: '' }); 
                            if(formErrors.subject) setFormErrors({...formErrors, subject: false}); 
                            setSubjectWarning(false);
                        }}
                    >
                      <option value="">Предмет</option>
                      {subjects.map(sub => <option key={sub.id} value={sub.id}>{sub.name}</option>)}
                    </select>
                  </div>

                  <select className="px-4 py-3 rounded-xl border border-gray-200 text-sm font-bold bg-white" value={newQuestion.class_level} onChange={(e) => setNewQuestion({ ...newQuestion, class_level: parseInt(e.target.value) })}>
                    {[5, 6, 7, 8, 9, 10, 11].map(lvl => <option key={lvl} value={lvl}>{lvl} класс</option>)}
                  </select>
                  <select className="px-4 py-3 rounded-xl border border-gray-200 text-sm font-bold bg-white" value={newQuestion.difficulty} onChange={(e) => setNewQuestion({ ...newQuestion, difficulty: e.target.value })}>
                    {['Базовый', 'Повышенный', 'Высокий'].map(diff => <option key={diff} value={diff}>{diff}</option>)}
                  </select>
                  <div className="text-sm font-bold w-full h-[46px]">
                    <TopicSelect 
                      selectedSubjectId={newQuestion.subject} 
                      value={newQuestion.topic} 
                      onChange={(val) => setNewQuestion({ ...newQuestion, topic: val })} 
                      currentUser={userData} 
                      className="w-full h-full px-4 py-3 rounded-xl border border-gray-200 bg-white outline-none hover:bg-gray-50 transition-colors" 
                      onDisabledClick={handleTopicDisabledClick}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 border-t border-gray-100 pt-6">
                  {newQuestion.answers.map((answer, index) => (
                    <div key={index} className={`flex items-center gap-2 p-3 rounded-xl border transition-all ${formErrors[`answer_${index}`] ? 'border-red-500 bg-red-50 ring-2 ring-red-200' : answer.is_correct ? 'border-green-400 bg-green-50 shadow-sm' : 'border-gray-200 bg-gray-50 hover:bg-white'}`}>
                      <input type="radio" name="correct-answer-manual" checked={answer.is_correct} onChange={() => handleCorrectAnswerChange(index)} className="w-4 h-4 text-green-600 cursor-pointer flex-shrink-0" />
                      <span className={`text-xs font-black flex-shrink-0 ${formErrors[`answer_${index}`] ? 'text-red-500' : 'text-gray-400'}`}>{answerLabels[index]})</span>
                      <div className="flex-1 min-w-0">
                        {editingManualAnsIdx === index ? (
                          <input type="text" className={`w-full bg-transparent border-b-2 border-blue-400 outline-none text-xs font-semibold pb-1 ${formErrors[`answer_${index}`] ? 'text-red-700' : 'text-gray-800'}`} placeholder="Введите ответ..." value={answer.text} onChange={(e) => handleAnswerTextChange(index, e.target.value)} autoFocus onBlur={() => setEditingManualAnsIdx(-1)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditingManualAnsIdx(-1); } }} />
                        ) : (
                          <div onClick={() => setEditingManualAnsIdx(index)} className="cursor-pointer py-1.5 min-h-[30px] flex items-center" title="Нажмите для редактирования">
                            {answer.text ? <MathText text={autoFormatMath(answer.text)} /> : <span className="text-xs text-gray-400 italic">Нажмите, чтобы ввести</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-4 border-t border-gray-100">
                  <button type="submit" className={`w-full py-4 text-white text-xs font-black rounded-xl uppercase tracking-widest transition-all shadow-md flex justify-center items-center gap-2 ${newQuestion.id ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-blue-600 hover:bg-blue-700'}`}>
                    <span>💾</span> {newQuestion.id ? 'Сохранить изменения' : 'Сохранить в личный банк'}
                  </button>
                  {newQuestion.id && (
                    <button type="button" onClick={handleCancelEdit} className="w-full sm:w-48 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-black rounded-xl uppercase tracking-widest transition-all shadow-sm">
                      ✕ Отменить
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  // =========================================================================
  // РЕНДЕР 6: СПИСОК ВОПРОСОВ (НА ВСЮ ШИРИНУ ЭКРАНА)
  // =========================================================================
  const renderListMode = () => (
    <div className="max-w-[1600px] mx-auto w-full">
        <div className="flex justify-between items-center bg-white p-5 rounded-3xl border border-gray-200 shadow-sm mb-5">
          <h1 className="text-2xl font-black text-gray-800 pl-2">📚 Банк вопросов</h1>
          <button onClick={() => navigate('/')} className="bg-gray-100 hover:bg-gray-200 text-xs font-bold px-5 py-2.5 rounded-xl transition-colors shadow-sm">← Главная</button>
        </div>

        <div className="flex flex-wrap gap-2 bg-gray-100 p-2 rounded-2xl border border-gray-200 shadow-inner mb-5">
          <button onClick={() => setActiveTab('all')} className={`flex-1 min-w-[80px] py-2.5 px-4 text-xs font-bold rounded-xl transition-all ${activeTab === 'all' ? 'bg-gray-800 text-white shadow-md' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200'}`}>Все</button>
          <button onClick={() => setActiveTab('private')} className={`flex-1 min-w-[80px] py-2.5 px-4 text-xs font-bold rounded-xl transition-all ${activeTab === 'private' ? 'bg-purple-600 text-white shadow-md' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200'}`}>Мои</button>
          <button onClick={() => setActiveTab('public')} className={`flex-1 min-w-[80px] py-2.5 px-4 text-xs font-bold rounded-xl transition-all ${activeTab === 'public' ? 'bg-green-500 text-white shadow-md' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200'}`}>Общие</button>
          {isAdmin && <button onClick={() => setActiveTab('blocked')} className={`flex-1 min-w-[120px] py-2.5 px-4 text-xs font-bold rounded-xl transition-all ${activeTab === 'blocked' ? 'bg-red-100 text-red-600 shadow-md' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-200'}`}>Заблокированные</button>}
        </div>

        <div className="flex flex-col md:flex-row gap-3 mb-5">
          <div className="flex gap-3 flex-1">
            <select className="bg-white border border-gray-200 text-gray-700 px-4 py-3.5 rounded-2xl text-xs font-bold outline-none flex-1 shadow-sm focus:border-blue-400" value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)}>
              <option value="">Все предметы</option>{subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {isAdmin && (
              <select className="bg-white border border-gray-200 text-gray-700 px-4 py-3.5 rounded-2xl text-xs font-bold outline-none flex-1 shadow-sm focus:border-blue-400" value={filterCreator} onChange={(e) => setFilterCreator(e.target.value)}>
                <option value="">Все авторы</option>{creatorsList.map(u => <option key={u.id} value={u.id}>{u.username || u.first_name}</option>)}
              </select>
            )}
          </div>
          <div className="relative flex-1 md:flex-none md:w-72">
            <input type="text" placeholder="Поиск по тексту..." className="w-full bg-white border border-gray-200 rounded-2xl pl-11 pr-4 py-3.5 text-xs font-bold outline-none focus:border-blue-400 shadow-sm" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            <svg className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayQuestions.length === 0 && !loading && (
            <div className="col-span-full py-20 text-center text-gray-400 font-bold border-2 border-dashed border-gray-200 rounded-[2rem] bg-white">По вашему запросу ничего не найдено</div>
          )}
          
          {displayQuestions.map((q) => {
            const isMine = q.creator === currentUserId;
            const subjectObj = subjects.find(s => s.id === q.subject);
            
            return (
              <div key={q.id} className="bg-white rounded-[2rem] p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all flex flex-col h-full group relative overflow-hidden">
                {q.is_blocked && ( <div className="absolute top-0 right-0 bg-red-500 text-white text-[9px] font-black px-4 py-1.5 rounded-bl-xl uppercase tracking-widest shadow-sm">Заблокирован</div> )}
                <div className="flex justify-between items-center mb-5">
                  <div className="flex gap-2 flex-wrap">
                    <span className="bg-gray-100 text-gray-600 border border-gray-200 text-[9px] font-black px-2.5 py-1 rounded-lg uppercase tracking-widest">{subjectObj ? subjectObj.name : 'Без предмета'} • {q.class_level} кл.</span>
                    <span className={`text-[9px] font-black px-2.5 py-1 rounded-lg border uppercase tracking-widest ${q.difficulty === 'Базовый' ? 'bg-green-50 text-green-700 border-green-200' : q.difficulty === 'Повышенный' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{q.difficulty || 'Базовый'}</span>
                    {q.is_public && <span className="bg-blue-50 text-blue-700 border border-blue-200 text-[9px] font-black px-2.5 py-1 rounded-lg uppercase tracking-widest">Общий</span>}
                  </div>
                  {(isAdmin || isMine) && (
                    <button onClick={() => handleDeleteQuestion(q.id)} className="text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors bg-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 absolute top-4 right-4">✕</button>
                  )}
                </div>
                
                <h3 className="font-bold text-gray-800 text-sm mb-5 leading-relaxed line-clamp-4 min-h-[60px]">
                  {q.text ? <MathText text={autoFormatMath(q.text)} /> : <span className="text-gray-400 italic text-xs">Текст отсутствует</span>}
                </h3>
                {q.image && (
                  <div className="mb-5 bg-gray-50 rounded-xl p-2 border border-gray-100">
                    <img src={q.image} alt="Вопрос" className="max-h-32 object-contain mx-auto rounded" />
                  </div>
                )}

                <div className="space-y-2 mb-6 flex-1 pl-4 border-l-2 border-blue-100">
                  {q.answers && q.answers.map((ans, idx) => (
                    <div key={idx} className={`text-xs font-semibold flex items-center gap-3 py-1 transition-colors ${ans.is_correct ? 'text-green-700 font-black' : 'text-gray-500'}`}>
                      <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 font-black ${ans.is_correct ? 'bg-green-600 text-white shadow-sm' : 'bg-gray-100 text-gray-400'}`}>
                        {answerLabels[idx]})
                      </span>
                      <div className="truncate"><MathText text={ans.text} /></div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-5 mt-auto">
                  {isMine || isAdmin ? (
                    <>
                      <button onClick={() => handleEditQuestion(q)} className="flex-1 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 border border-yellow-200 font-bold text-[10px] py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5">
                        <span>✏️</span> Изменить
                      </button>
                      {isAdmin ? (
                        <button onClick={() => handleToggleBlock(q.id)} className="flex-1 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 font-bold text-[10px] py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5">
                          <span>🚫</span> Блок
                        </button>
                      ) : (
                        !q.is_public && !q.on_moderation && (
                          <button onClick={() => handleSendToModeration(q.id)} className="flex-1 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 font-bold text-[10px] py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5">
                            <span>🚀</span> В паблик
                          </button>
                        )
                      )}
                    </>
                  ) : (
                    <>
                      <button onClick={() => handleCloneQuestion(q.id)} className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 font-bold text-[10px] py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5">
                        <span>📥</span> В мой банк
                      </button>
                      {isAdmin && !q.is_public && q.on_moderation && (
                        <button onClick={() => handleApproveQuestion(q.id)} className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 font-bold text-[10px] py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5">
                          <span>✅</span> Одобрить
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {displayTotalPages > 1 && (
          <div className="flex items-center justify-center gap-4 bg-white p-4 rounded-3xl border border-gray-200 shadow-sm w-fit mx-auto mt-8">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-5 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-black rounded-xl disabled:opacity-50 transition-colors">
              ← Назад
            </button>
            <span className="text-xs font-black text-gray-500 bg-gray-100 px-4 py-2.5 rounded-xl border border-gray-200">
              Стр. {currentPage} из {displayTotalPages}
            </span>
            <button onClick={() => setCurrentPage(p => Math.min(displayTotalPages, p + 1))} disabled={currentPage === displayTotalPages} className="px-5 py-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-black rounded-xl disabled:opacity-50 transition-colors">
              Вперед →
            </button>
          </div>
        )}
    </div>
  );

  // =========================================================================
  // MAIN RETURN
  // =========================================================================
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 sm:p-6 font-sans relative pb-20">
      {toast && (
        <div className={`fixed bottom-8 right-8 px-6 py-4 rounded-2xl shadow-2xl z-50 text-sm font-bold text-white transform transition-all duration-300 animate-slide-up ${toast.type === 'error' ? 'bg-red-500' : 'bg-gray-900'}`}>
          <div className="whitespace-pre-line">{toast.message}</div>
        </div>
      )}

      {draftQuestions.length > 0 
          ? renderDrafts() 
          : isSummaryMode 
              ? renderSummaryMode()
              : isAddMode 
                  ? renderAddMode()
                  : renderListMode()
      }
    </div>
  );
}

export default QuestionsManager;